import React, {
  useEffect,
  useRef,
  useState,
  MouseEvent,
  SetStateAction,
  Dispatch,
  useCallback,
} from 'react';

import tippy from 'tippy.js';
import classNames from 'classnames';
import { useSelector } from 'react-redux';
import htmlParser from 'html-react-parser';
import { Button } from '@praktio/common-ui';
import { Constants, Utils } from '@praktio/common';
import { head, cloneDeep, throttle, isEqual } from 'lodash';

import LoremComponent from './LoremComponent';
import { RootState } from '../../ducks/configureStore';
import Document from '../../../src/components/Document';
import { updateResource } from '../../ducks/slidesLitmosReducer';
import { wrapElementsInAnnotationSpan } from '../../utils/annotations';

import {
  addAnnotationsToContent,
  processDocument_v2,
  hashId,
  cleanContent,
  isLayeredAnnotation,
  isCBSSlide,
} from '../../utils/slide';

import {
  LoremLengths,
  AnnotationOffscreenPositions,
  SupplementalDocStates,
  WindowSizes,
} from '../../utils/constants';

import 'tippy.js/dist/tippy.css';
import 'tippy.js/dist/border.css';
import './TextLoader.scss';

const { isBaseAnnotation } = Utils.Functions;
const { TemplateTypes, CataTypes, AnnotationTypes, ScrollableAnnotations, ExcerptPositions } =
  Constants;
const CATAEditTypes = [CataTypes.EditHorizontal, CataTypes.EditVertical];
const AnnotationEnabledTypes = [
  TemplateTypes.Explanation,
  TemplateTypes.ClickBestSpot,
  TemplateTypes.CheckAllApply,
];

interface IProps {
  className?: string;
  isMainPanel?: boolean;
  newlyExpandedExplanation?: boolean;
  panelId?: string;
  resource?: Annotation;
  sharedWidth?: boolean;
  showCollapsiblePanel?: boolean;
  shouldPulse?: boolean;
  windowSize: WindowSizes;
  hasSupplementalDoc: boolean;
  supplementalDocState?: SupplementalDocStates;
  onSelect?: (event: MouseEvent) => void;
  setNewlyExpandedExplanation?: Dispatch<SetStateAction<boolean>>;
}

interface IMeasurementById {
  [id: string]: IRect;
}

interface IRect {
  bottom: number;
  top: number;
  left: number;
  right: number;
  height: number;
  width: number;
}

interface IInactiveInterface {
  [id: string]: Annotation;
}

interface IAnnotationClosestOff {
  id: string;
  rect: IRect;
}

interface IScrollData {
  offscreenPosition?: string;
  minRect?: IRect;
  maxRect?: IRect;
  id?: string;
  ids?: string[];
}

interface IScrollDataVertical extends IScrollData {
  closestOffTop?: IAnnotationClosestOff;
  closestOffBottom?: IAnnotationClosestOff;
}

interface IScrollDataHorizontal extends IScrollData {
  closestOffLeft?: IAnnotationClosestOff;
  closestOffRight?: IAnnotationClosestOff;
}
interface IAnnotationOffscreenPosition {
  vertical?: IScrollDataVertical;
  horizontal?: IScrollDataHorizontal;
}

const emptyRect = (): IRect => ({
  top: 0,
  bottom: 0,
  left: 0,
  right: 0,
  width: 0,
  height: 0,
});

const getRects = (
  isCBS: boolean,
  annotation: Annotation,
  vertsById: IMeasurementById,
  horsById: IMeasurementById,
  doc: HTMLDivElement,
) => {
  if (!doc) {
    return;
  }

  const { startIndex, endIndex, rootId, _id, elementId, subType } = annotation;

  // TODO - remove casting as 'any' when Praktio/Common typings are updated.
  const nonBase = !isBaseAnnotation(annotation as any);
  if ((startIndex || startIndex === 0) && (endIndex || endIndex === 0)) {
    let startElement;
    let startRect: IRect | undefined;
    let index = startIndex;
    // Iterate startIndex forward until we find a visible element or reach endIndex
    while ((!startElement || !startRect?.height || !startRect?.width) && index <= endIndex) {
      const sIndex = `${nonBase ? rootId + '-' : ''}${index}`;
      startElement = doc.querySelector(`span[data-id="${isCBS ? hashId(sIndex) : sIndex}"]`);
      index += 1;
      if (startElement) {
        startRect = startElement.getBoundingClientRect();
      }
    }

    let rect: IRect | undefined;
    let endElement;
    let endRect: IRect | undefined;
    if (startElement) {
      if (!rect) {
        rect = emptyRect();
      }

      const startRect = startElement.getBoundingClientRect();
      rect.top = startRect.top;
      rect.bottom = startRect.bottom;
      rect.height = startRect.height;
      rect.left = startRect.left;
      rect.right = startRect.right;
      rect.width = startRect.width;
      index = endIndex;
      // Iterate endIndex backward until we find a visible element or reach startIndex
      while ((!endElement || !endRect?.height || !endRect?.width) && index > startIndex) {
        const eIndex = `${nonBase ? rootId + '-' : ''}${index}`;
        endElement = doc.querySelector(`span[data-id="${isCBS ? hashId(eIndex) : eIndex}"]`);
        index -= 1;
        if (endElement) {
          endRect = endElement.getBoundingClientRect();
        }
      }
    }

    if (endElement) {
      if (!rect) {
        rect = emptyRect();
      }

      endRect = endElement.getBoundingClientRect();
      // Vertical boundary checks to build/extend rectangle
      if (!rect.top || rect.top > endRect.top) {
        rect.top = endRect.top;
      }

      if (!rect.bottom || rect.bottom < endRect.bottom) {
        rect.bottom = endRect.bottom;
      }

      if (!rect.height || rect.height < endRect.height) {
        rect.height = endRect.height;
      }

      // Horizontal boundary checks to build/extend rectangle
      if (!rect.left || rect.left > endRect.left) {
        rect.left = endRect.left;
      }

      if (!rect.right || rect.right < endRect.right) {
        rect.right = endRect.right;
      }

      if (!rect.width || rect.width < endRect.width) {
        rect.width = endRect.width;
      }
    }

    // Only return rects that exist in the DOM
    if (_id && rect) {
      vertsById[_id] = rect;
      horsById[_id] = rect;
    }
  } else if (rootId && elementId) {
    const isAddition = subType === AnnotationTypes.Addition_v2;
    const element = doc.querySelector(`#${isAddition ? elementId : rootId}`);
    if (element) {
      const rect = element.getBoundingClientRect();
      if (_id) {
        vertsById[_id] = rect;
        horsById[_id] = rect;
      }
    }
  }
};

const hideForModeCheckPassed = (annotation: Annotation, activityComplete: boolean): boolean => {
  if (
    (!annotation.hideForQuiz && !annotation.hideForFeedback) ||
    (annotation.hideForQuiz && activityComplete) ||
    (annotation.hideForFeedback && !activityComplete)
  ) {
    return true;
  }
  return false;
};

const findValidAnnotations = ({
  slideType,
  annotations,
  activityComplete,
  paragraphSelection,
}: {
  slideType?: string;
  annotations?: Annotation[];
  activityComplete: boolean;
  paragraphSelection?: boolean;
}) => {
  let latestStartTime: any;
  let baseAnnotations: Annotation[] = [];
  let nonBaseAnnotations: Annotation[] = [];
  let inactiveByRootId: IInactiveInterface = {};

  annotations?.forEach((timelineElement: Annotation) => {
    let isValid = false;
    if (
      ScrollableAnnotations.includes(timelineElement.subType || '') &&
      !timelineElement.ignoreAutoscroll
    ) {
      if (slideType === TemplateTypes.ClickBestSpot) {
        // For CBS, annotations can only be scroll to when the activity is complete (Feedback mode)
        // and in that situation, only for a Correct annotation unless paragraphSelection is true,
        // in which case, it's a Choice annotation with the correct flag set to true.
        if (
          activityComplete &&
          (timelineElement.subType === AnnotationTypes.Correct ||
            (paragraphSelection &&
              ((timelineElement.subType === AnnotationTypes.Choice && timelineElement.correct) ||
                timelineElement.subType === AnnotationTypes.Correct)))
        ) {
          if (hideForModeCheckPassed(timelineElement, activityComplete)) {
            isValid = true;
          }
        }
      } else if (
        (![AnnotationTypes.Delete_v2, AnnotationTypes.Addition_v2].includes(
          timelineElement.subType || '',
        ) ||
          timelineElement.showStyling ||
          (timelineElement.subType === AnnotationTypes.Addition_v2 &&
            timelineElement.includeHighlight)) &&
        (slideType !== TemplateTypes.Explanation || timelineElement.active)
      ) {
        if (slideType === TemplateTypes.Explanation) {
          const aStart = parseFloat(head(timelineElement.range) ?? '');
          if ((!latestStartTime && latestStartTime !== 0) || aStart > latestStartTime) {
            latestStartTime = aStart;
          }
        }
        // Before setting isValid to true, make sure that the hideForQuiz/hideForFeedback
        // conditions are met.
        if (hideForModeCheckPassed(timelineElement, activityComplete)) {
          isValid = true;
        }
      }
    }

    if (isValid) {
      // TODO - remove casting as 'any' when praktio/common is updated
      if (isBaseAnnotation(timelineElement as any)) {
        baseAnnotations.push(timelineElement);
      } else {
        nonBaseAnnotations.push(timelineElement);
      }
    } else if (timelineElement.elementId && !timelineElement.active) {
      inactiveByRootId[timelineElement.elementId] = timelineElement;
    }
  });
  // For Explanation, find most recently active annotations and filter out
  // any span-level annotations on element-level where the element-level
  // is hidden
  if (slideType === TemplateTypes.Explanation) {
    baseAnnotations = baseAnnotations.filter((baseAnnotation) => {
      const aStart = parseFloat(head(baseAnnotation.range) ?? '');
      return aStart === latestStartTime;
    });

    nonBaseAnnotations = nonBaseAnnotations.filter((nonBaseAnnotation) => {
      const aStart = parseFloat(head(nonBaseAnnotation.range) ?? '');
      if (
        (nonBaseAnnotation.startIndex || nonBaseAnnotation.startIndex === 0) &&
        (nonBaseAnnotation.endIndex || nonBaseAnnotation.endIndex === 0) &&
        nonBaseAnnotation.rootId &&
        inactiveByRootId[nonBaseAnnotation.rootId]
      ) {
        return false;
      }
      return aStart === latestStartTime;
    });
  }

  return {
    baseAnnotations,
    nonBaseAnnotations,
  };
};

const annotationOffscreenPosition = ({
  documentElementRef,
  lastScrolledVertDataRef,
  lastScrolledHorzDataRef,
  annotations,
  slideType,
  paragraphSelection,
  activityComplete,
}: {
  documentElementRef?: any;
  lastScrolledVertDataRef: React.MutableRefObject<IScrollData | undefined>;
  lastScrolledHorzDataRef: React.MutableRefObject<IScrollData | undefined>;
  annotations: Annotation[];
  slideType: string | undefined;
  paragraphSelection: boolean | undefined;
  activityComplete: boolean;
}): IAnnotationOffscreenPosition | undefined => {
  // If there is no DOM element that is using the documentElementRef, return undefined.
  if (!documentElementRef || !documentElementRef.current) {
    return;
  }

  const doc = documentElementRef.current;

  // Check if the element contains img tags and if so,
  // verify they are loaded before running auto-scroll code.
  // Otherwise, bail out and setState to re-run componentDidUpdate
  let images;
  if (doc) {
    images = doc.querySelectorAll('img');
  }
  if (images && images.length) {
    for (const image of images) {
      if (!image.complete || !image.naturalHeight) {
        lastScrolledVertDataRef.current = undefined;
        lastScrolledHorzDataRef.current = undefined;
        return;
      }
    }
  }

  const { baseAnnotations, nonBaseAnnotations } = findValidAnnotations({
    slideType,
    annotations,
    paragraphSelection,
    activityComplete,
  });

  const vertsById: IMeasurementById = {};
  const horsById: IMeasurementById = {};
  const container = doc.getBoundingClientRect();
  const isCBS = slideType === TemplateTypes.ClickBestSpot;
  const validAnnotations = [...baseAnnotations, ...nonBaseAnnotations];
  validAnnotations.forEach((annotation) => getRects(isCBS, annotation, vertsById, horsById, doc));

  let verticalMin: number | undefined;
  let verticalMax: number | undefined;
  let verticalMinRect = emptyRect();
  let verticalMaxRect = emptyRect();
  let vertId: string | undefined;
  const vertIds = Object.keys(vertsById);
  let closestTop: number | undefined;
  let closestBottom: number | undefined;
  let closestOffTop: IAnnotationClosestOff | undefined;
  let closestOffBottom: IAnnotationClosestOff | undefined;
  vertIds.forEach((k) => {
    const rect = vertsById[k];
    const top = rect.top;
    if (!verticalMin || top < verticalMin) {
      verticalMin = top;
      verticalMinRect = rect;
      vertId = k;
    }

    const bottom = rect.bottom;
    if (!verticalMax || bottom > verticalMax) {
      verticalMax = bottom;
      verticalMaxRect = rect;
    }

    // Find the annotation that is offscreen and closest
    // to the container
    // For top of container
    if (top < container.top || bottom < container.top) {
      const value = bottom < container.top ? Math.max(top, bottom) : top;
      if (!closestTop || value > closestTop) {
        closestTop = value;
        closestOffTop = { id: k, rect };
      }
    }
    // For bottom of container
    // This is an else-if because we only want to handle annotations that
    // are either off on top or bottom, but not both
    else if (bottom > container.bottom || top > container.bottom) {
      const value = top > container.bottom ? Math.min(top, bottom) : bottom;
      if (!closestBottom || value < closestBottom) {
        closestBottom = value;
        closestOffBottom = { id: k, rect };
      }
    }
  });

  let verticalOffscreenPosition: string | undefined;
  let verticalMinRectToReturn: IRect | undefined;
  let verticalMaxRectToReturn: IRect | undefined;
  let horizontalOffscreenPosition: string | undefined;
  let horizontalMinRectToReturn: IRect | undefined;
  let horizontalMaxRectToReturn: IRect | undefined;

  if (verticalMinRect && verticalMaxRect && validAnnotations.length > 0) {
    if (verticalMinRect.top < container.top) {
      verticalOffscreenPosition = AnnotationOffscreenPositions.Top;
    } else if (verticalMaxRect.bottom > container.bottom) {
      verticalOffscreenPosition = AnnotationOffscreenPositions.Bottom;
    }
    verticalMinRectToReturn = verticalMinRect;
    verticalMaxRectToReturn = verticalMaxRect;
  }

  let horizontalMin: number | undefined;
  let horizontalMax: number | undefined;
  let horizontalMinRect = emptyRect();
  let horizontalMaxRect = emptyRect();
  let horId: string | undefined;
  const horIds = Object.keys(horsById);
  let closestLeft: number | undefined;
  let closestRight: number | undefined;
  let closestOffLeft: IAnnotationClosestOff | undefined;
  let closestOffRight: IAnnotationClosestOff | undefined;
  horIds.forEach((k) => {
    const rect = horsById[k];
    const left = rect.left;
    if (!horizontalMin || left < horizontalMin) {
      horizontalMin = left;
      horizontalMinRect = rect;
      horId = k;
    }

    const right = rect.right;
    if (!horizontalMax || right > horizontalMax) {
      horizontalMax = right;
      horizontalMaxRect = rect;
      horId = k;
    }

    // Find the annotation that is offscreen and closest
    // to the container
    // For left of container
    if (left < container.left) {
      if (!closestLeft || left > closestLeft) {
        closestLeft = left;
        closestOffLeft = { id: k, rect };
      }
    }
    // For right of container
    else if (right > container.right) {
      if (!closestRight || right < closestRight) {
        closestRight = right;
        closestOffRight = { id: k, rect };
      }
    }
  });

  if (horizontalMinRect && horizontalMaxRect && validAnnotations.length > 0) {
    if (horizontalMinRect.left < container.left) {
      horizontalOffscreenPosition = AnnotationOffscreenPositions.Left;
    } else if (horizontalMaxRect.right > container.right) {
      horizontalOffscreenPosition = AnnotationOffscreenPositions.Right;
    }
    horizontalMinRectToReturn = horizontalMinRect;
    horizontalMaxRectToReturn = horizontalMaxRect;
  }

  return {
    vertical: {
      offscreenPosition: verticalOffscreenPosition,
      minRect: verticalMinRectToReturn,
      maxRect: verticalMaxRectToReturn,
      id: vertId,
      ids: vertIds,
      closestOffTop,
      closestOffBottom,
    },
    horizontal: {
      offscreenPosition: horizontalOffscreenPosition,
      minRect: horizontalMinRectToReturn,
      maxRect: horizontalMaxRectToReturn,
      id: horId,
      ids: horIds,
      closestOffLeft,
      closestOffRight,
    },
  };
};

type SetClosestOff = React.Dispatch<React.SetStateAction<IAnnotationClosestOff | undefined>>;
const updateScrollToAnnotationButton = ({
  documentElementRef,
  lastScrolledVertDataRef,
  lastScrolledHorzDataRef,
  annotations,
  slideType,
  paragraphSelection,
  activityComplete,
  setClosestOffTop,
  setClosestOffBottom,
  setClosestOffLeft,
  setClosestOffRight,
  isLandscape,
  mainDocDisabled,
}: {
  documentElementRef?: any;
  lastScrolledVertDataRef: React.MutableRefObject<IScrollData | undefined>;
  lastScrolledHorzDataRef: React.MutableRefObject<IScrollData | undefined>;
  annotations: Annotation[];
  slideType: string | undefined;
  paragraphSelection: boolean | undefined;
  activityComplete: boolean;
  setClosestOffTop: SetClosestOff;
  setClosestOffBottom: SetClosestOff;
  setClosestOffLeft: SetClosestOff;
  setClosestOffRight: SetClosestOff;
  isLandscape: boolean;
  mainDocDisabled: boolean;
}) => {
  const isEnabled =
    !mainDocDisabled &&
    (slideType === TemplateTypes.CheckAllApply ||
      (slideType === TemplateTypes.ClickBestSpot && activityComplete));

  const annotationsOffScreen = isEnabled
    ? annotationOffscreenPosition({
        documentElementRef,
        lastScrolledVertDataRef,
        lastScrolledHorzDataRef,
        annotations,
        slideType,
        paragraphSelection,
        activityComplete: !!activityComplete,
      })
    : undefined;

  if (!annotationsOffScreen) {
    setClosestOffTop(undefined);
    setClosestOffBottom(undefined);
    setClosestOffLeft(undefined);
    setClosestOffRight(undefined);
    return;
  }

  const { vertical, horizontal } = annotationsOffScreen;
  if (!isLandscape) {
    if (vertical) {
      const { closestOffTop, closestOffBottom } = vertical;
      setClosestOffTop(closestOffTop);
      setClosestOffBottom(closestOffBottom);
    }
  } else {
    if (horizontal) {
      const { closestOffLeft, closestOffRight } = horizontal;
      setClosestOffLeft(closestOffLeft);
      setClosestOffRight(closestOffRight);
    }
  }
};

const ScrollToAnnotationButton = ({
  position,
  onClick,
  supplementalDocState,
  hasSupplementalDoc,
  docIsLandscape,
}: {
  position: string;
  onClick: () => void;
  supplementalDocState: SupplementalDocStates | undefined;
  hasSupplementalDoc: boolean;
  docIsLandscape: boolean;
}) => {
  let icon: string | undefined;
  switch (position) {
    case AnnotationOffscreenPositions.Top:
      icon = 'arrow_upward';
      break;
    case AnnotationOffscreenPositions.Bottom:
      icon = 'arrow_downward';
      break;
    case AnnotationOffscreenPositions.Left:
      icon = 'arrow_back';
      break;
    case AnnotationOffscreenPositions.Right:
      icon = 'arrow_forward';
      break;
    default:
      break;
  }

  const button = (
    <button
      className={classNames('ScrollToAnnotationButton')}
      aria-label={icon?.replace('_', ' ')}
      onClick={onClick}
    >
      <span className="material-icons" aria-hidden="true">
        {icon}
      </span>
    </button>
  );

  const wrappedButton = (
    <>
      <div className="landscape-button-wrapper">{button}</div>
      <div className="landscape-button-positioner"></div>
    </>
  );

  return (
    <div
      className={classNames('ScrollToAnnotationButtonWrapper', position, {
        'space-for-sup-doc-tab':
          supplementalDocState &&
          supplementalDocState === SupplementalDocStates.HalfOpen &&
          hasSupplementalDoc,
        'doc-is-landscape': docIsLandscape,
        'has-supplemental-doc': hasSupplementalDoc,
      })}
    >
      {docIsLandscape ? wrappedButton : button}
    </div>
  );
};

const TextLoader = ({
  className,
  isMainPanel,
  newlyExpandedExplanation,
  panelId,
  resource,
  sharedWidth,
  shouldPulse,
  windowSize,
  hasSupplementalDoc,
  supplementalDocState,
  onSelect,
  setNewlyExpandedExplanation,
}: IProps) => {
  // REDUX STATE
  const {
    activityComplete,
    annotations,
    currentSlide,
    displayedEditId,
    enableParagraphMarks,
    excerptPosition,
    hideExcerptLabel,
    outOfTries,
    paragraphSelection,
    pureTimeline,
    selected,
    singlePanel,
    slideSubType,
    slideType,
    stateType,
    triPanel,
  } = useSelector((state: RootState) => {
    const { slidesLitmos, quizLitmos } = state;
    const { currentSlide } = slidesLitmos;
    const { outOfTries, stateType, selected, displayedEditId } = quizLitmos;
    const {
      timeline: pureTimeline,
      panels = [],
      enableParagraphMarks,
      type: slideType,
      subType: slideSubType,
      hideExcerptLabel,
      paragraphSelection,
      excerptPosition,
      learnerData,
    } = currentSlide || {};
    const module = (slidesLitmos || {}).data || {};
    const triPanel = panels.length > 2;
    const singlePanel = panels.length === 1;
    const annotations = cloneDeep(
      (pureTimeline || []).filter((timelineElement) => timelineElement.panelId === panelId),
    );
    const { completed: activityComplete } = learnerData || {};

    return {
      activityComplete,
      annotations,
      currentSlide,
      displayedEditId,
      enableParagraphMarks,
      excerptPosition: excerptPosition || ExcerptPositions.Middle,
      hideExcerptLabel: hideExcerptLabel || module.hideExcerptLabel,
      outOfTries,
      paragraphSelection,
      pureTimeline,
      selected,
      singlePanel,
      slideSubType,
      slideType,
      stateType,
      triPanel,
    };
  });

  // LOCAL STATE
  const [showParagraphMarks, setShowParagraphMarks] = useState(false);
  const [closestOffTop, setClosestOffTop] = useState<IAnnotationClosestOff>();
  const [closestOffBottom, setClosestOffBottom] = useState<IAnnotationClosestOff>();
  const [closestOffLeft, setClosestOffLeft] = useState<IAnnotationClosestOff>();
  const [closestOffRight, setClosestOffRight] = useState<IAnnotationClosestOff>();
  const prevActivityCompleteStatusRef = useRef(activityComplete);

  // TODO - figure out how to not cast these refs as any.
  const documentElementRef = useRef() as any;
  const resourceRef = useRef(resource);

  // annotationsRef = Hacky solution for conditionally running updateScrollToAnnotationButton in useEffect
  // The annotationsRef is kept in line with annotationsToUse below, but is used to
  // determine if updateScrollToAnnotationButton should run inside the useEffect because
  // the scroll handler bound there can get triggered before the useEffect has a chance
  // to clean it up when annotationsToUse changes.
  const annotationsRef = useRef(annotations);
  const lastScrolledVertDataRef = useRef<IScrollData | undefined>();
  const lastScrolledHorzDataRef = useRef<IScrollData | undefined>();
  const displayedEditIdRef = useRef<string | undefined>();
  const annotationIdToTippyInstance = useRef<IKey>({});

  const showExplain = outOfTries ? 'show-explain' : '';
  const textClass = className;
  const isBestEdit = CATAEditTypes.includes(slideSubType || '');

  const answerBase = (currentSlide?.timeline || []).find(
    (timelineElement) =>
      timelineElement.subType === AnnotationTypes.AnswerBase && timelineElement.panelId === panelId,
  );

  let isBestEditContent;
  let annotationsToUse = annotations;
  annotationsRef.current = annotations;
  if (isBestEdit && answerBase && displayedEditId) {
    const choiceToDisplayEditsFor = pureTimeline?.find(
      (timelineElement) => timelineElement._id === displayedEditId,
    );

    if (choiceToDisplayEditsFor) {
      if (choiceToDisplayEditsFor.processedAnnotations) {
        annotationsToUse = choiceToDisplayEditsFor.processedAnnotations;
        annotationsRef.current = annotationsToUse;
      }

      isBestEditContent = outOfTries
        ? choiceToDisplayEditsFor.processedContentFeedback
        : choiceToDisplayEditsFor.processedContent;
    }
  }
  const resourceContent = resource?.snippedContent || resource?.indexedContent || resource?.content;

  const toggleParagraphMarks = () => {
    setShowParagraphMarks(!showParagraphMarks);
  };

  let answeredClass = '';
  if (slideType === TemplateTypes.ClickBestSpot && outOfTries) {
    answeredClass = stateType === 'correct' ? 'answered-correct' : 'answered-incorrect';
  }

  const hasSnippet = resource?.snippedContent;
  const isExcerpt = hasSnippet && !hideExcerptLabel;

  const getAnnotationOffscreenPositionAndScroll = throttle(
    () => {
      const annotationOffscreenPositionValues = annotationOffscreenPosition({
        documentElementRef,
        lastScrolledVertDataRef,
        lastScrolledHorzDataRef,
        annotations: annotationsToUse,
        slideType,
        paragraphSelection,
        activityComplete: !!activityComplete,
      });

      if (annotationOffscreenPositionValues) {
        scrollToAnnotation(annotationOffscreenPositionValues);
      }
    },
    500,
    { leading: true },
  );

  const scrollToSpecificAnnotation = useCallback(
    ({ closestOffData, position }: { closestOffData: IAnnotationClosestOff; position: string }) => {
      const doc = documentElementRef.current;
      const container = doc.getBoundingClientRect();
      const { rect } = closestOffData;
      // If position is Top, that means annotation is off at top, so scroll it to bottom of container
      if (position === AnnotationOffscreenPositions.Top) {
        doc.scrollTop += rect.bottom - container.top - container.height + 10;
      }
      // Else Bottom, annotation is off at bottom, so scroll it to the top of container
      else if (position === AnnotationOffscreenPositions.Bottom) {
        doc.scrollTop += rect.top - container.top - 10;
      }
      // Else Left, annotation is off at left, so scroll it to the right of container
      else if (position === AnnotationOffscreenPositions.Left) {
        doc.scrollLeft += rect.right - container.left - container.width + 10;
      }
      // Else Right, annotation is off at right, so scroll it to the left of container
      else if (position === AnnotationOffscreenPositions.Right) {
        doc.scrollLeft += rect.left - container.left - 10;
      }
    },
    [],
  );

  const scrollToAnnotation = useCallback(
    (annotationOffscreenPositionValues: IAnnotationOffscreenPosition) => {
      const doc = documentElementRef.current;
      const container = doc.getBoundingClientRect();

      if (!annotationOffscreenPositionValues) {
        return;
      }

      const { vertical, horizontal } = annotationOffscreenPositionValues;
      const lastScrolledVertical = lastScrolledVertDataRef.current;
      const lastScrolledHorizontal = lastScrolledHorzDataRef.current;
      // Vertical Scroll
      if (
        vertical?.offscreenPosition &&
        (vertical?.minRect?.height || vertical?.minRect?.top) &&
        (!lastScrolledVertical || !lastScrolledVertical?.ids?.includes(vertical.id || ''))
      ) {
        // Note: Results from getBoundingClientRect are referenced based on
        // the viewport (basically, top of the UI window). If doc is scrolled
        // and scroll is true, then to scroll to the correct position, we need
        // to add the difference between minRect and container (could be
        // positivie or negative) to account for the additional amount to scroll
        doc.scrollTop += vertical.minRect.top - container.top - 10;
        lastScrolledVertDataRef.current = vertical;
      }

      // Horizontal Scroll
      if (
        horizontal?.offscreenPosition &&
        (horizontal?.minRect?.width || horizontal?.minRect?.left) &&
        (!lastScrolledHorizontal || !lastScrolledHorizontal?.ids?.includes(horizontal.id || ''))
      ) {
        // Note: Same as comment for scrollTop, but for left instead of top
        doc.scrollLeft += horizontal.minRect.left - container.left - 10;
        lastScrolledHorzDataRef.current = horizontal;
      }
    },
    [],
  );

  const clearVertHorsIdsAndScrollToAnnotation = useCallback(() => {
    lastScrolledVertDataRef.current = undefined;
    lastScrolledHorzDataRef.current = undefined;
    getAnnotationOffscreenPositionAndScroll();
  }, [getAnnotationOffscreenPositionAndScroll]);

  const supplementalDocStateRef = useRef(supplementalDocState);

  const mainDocDisabled =
    !!hasSupplementalDoc &&
    supplementalDocState === SupplementalDocStates.FullyOpen &&
    windowSize !== WindowSizes.Large;

  const closeToolTips = () => {
    var toolTips = document.querySelectorAll('div[data-tippy-root]');

    if (toolTips.length <= 0) {
      return;
    }

    toolTips.forEach((toolTip) => toolTip?.remove());
  };

  useEffect(() => {
    if (supplementalDocStateRef.current !== supplementalDocState) {
      // TODO - create an out-of-scrope updatescrolltoannotationbutton function
      // that is updated whenever any of its dependent variables are changed using
      // a useEffect. That function SHOULD then be callable from any in-scope
      // functions when needed.
      updateScrollToAnnotationButton({
        documentElementRef,
        lastScrolledVertDataRef,
        lastScrolledHorzDataRef,
        annotations: annotationsToUse,
        slideType,
        paragraphSelection,
        activityComplete: !!activityComplete,
        setClosestOffTop,
        setClosestOffBottom,
        setClosestOffLeft,
        setClosestOffRight,
        isLandscape: !!resource?.isLandscape,
        mainDocDisabled: !!mainDocDisabled,
      });
    }
    supplementalDocStateRef.current = supplementalDocState;
  }, [
    supplementalDocState,
    activityComplete,
    annotationsToUse,
    paragraphSelection,
    slideType,
    resource?.isLandscape,
    mainDocDisabled,
  ]);

  // Scroll Check useEffect
  useEffect(() => {
    const docElRef = documentElementRef.current;
    const scrollHandler = () => {
      // This handler can sometimes get called before the useEffect
      // has time to process the annotationsToUse change, so we need to
      // compare against annotationsRef.current to make sure they are equal
      // otherwise annotationsToUse is old data and we should not run the function.
      if (isEqual(annotationsToUse, annotationsRef.current)) {
        updateScrollToAnnotationButton({
          documentElementRef,
          lastScrolledVertDataRef,
          lastScrolledHorzDataRef,
          annotations: annotationsToUse,
          slideType,
          paragraphSelection,
          activityComplete: !!activityComplete,
          setClosestOffTop,
          setClosestOffBottom,
          setClosestOffLeft,
          setClosestOffRight,
          isLandscape: !!resource?.isLandscape,
          mainDocDisabled: !!mainDocDisabled,
        });
      }
    };
    if (docElRef) {
      docElRef.addEventListener('scroll', scrollHandler);
      window.addEventListener('resize', scrollHandler);
    }
    return () => {
      if (docElRef) {
        docElRef.removeEventListener('scroll', scrollHandler);
        window.removeEventListener('resize', scrollHandler);
      }
    };
    // Disabling eslint warning because we only want to run with these specific dependencies
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [activityComplete, resource, annotationsToUse]);

  // Scroll to the annotation if landing on an already completed slide
  useEffect(() => {
    if (outOfTries) {
      const correctResource = annotations?.find(
        ({ subType }) => subType === AnnotationTypes.Correct,
      );
      const resource = {
        ...correctResource,
        className: stateType === 'correct' ? AnnotationTypes.Correct : AnnotationTypes.Incorrect,
        active: true,
      };

      setTimeout(() => updateResource(resource));
    }
    // Disabling eslint warning because we only want to run once (empty array)
    // eslint-disable-next-line react-hooks/exhaustive-deps

    return () => closeToolTips();
  }, []);

  // When CATA choice changes, clear prevVertIds/prevHorsIds and run scrollToAnnotation
  useEffect(() => {
    if (
      slideType === TemplateTypes.CheckAllApply &&
      displayedEditId !== displayedEditIdRef.current
    ) {
      setClosestOffTop(undefined);
      setClosestOffBottom(undefined);
      setClosestOffLeft(undefined);
      setClosestOffRight(undefined);
      clearVertHorsIdsAndScrollToAnnotation();
    }
    displayedEditIdRef.current = displayedEditId;
  }, [displayedEditId, slideType, clearVertHorsIdsAndScrollToAnnotation]);

  // Manually scroll to the annotation when newly landing on the feedback
  // part of a CATA slide.
  useEffect(() => {
    if (
      !prevActivityCompleteStatusRef.current &&
      activityComplete &&
      slideType === TemplateTypes.CheckAllApply
    ) {
      // Using a 300ms timeout to make sure that the autoscroll
      // takes the expanding answer pane into account.
      setTimeout(() => {
        clearVertHorsIdsAndScrollToAnnotation();
      }, 300);
    }
    prevActivityCompleteStatusRef.current = activityComplete;
  }, [activityComplete, slideType, clearVertHorsIdsAndScrollToAnnotation]);

  // If an explanation becomes expanded in CATA Vertical Feedback mode, call scrolltoannotation and set "newly expanded" value to false.
  useEffect(() => {
    if (newlyExpandedExplanation && setNewlyExpandedExplanation) {
      clearVertHorsIdsAndScrollToAnnotation();
      setNewlyExpandedExplanation(false);
    }
  }, [
    newlyExpandedExplanation,
    setNewlyExpandedExplanation,
    clearVertHorsIdsAndScrollToAnnotation,
  ]);

  // AutoScroll useEffect
  // DEV NOTE - For the autoscroll to behave properly, the document itself
  // must be the element that displays the scrollbar. I.E., if the parent
  // of the document is scrolling, the autoscroll will not work.
  useEffect(() => {
    let ignoreAutoScroll = false;

    // Check that there is a resource.
    // Check that the current resourceId is not the same as the last resourceId.
    // Check that documentElementRef is not null, nor its current value.
    // So: when the resource id CHANGES and there is already values in the documentElementRef, do:
    if (
      resource &&
      resource.resourceId !== resourceRef.current?.resourceId &&
      documentElementRef &&
      documentElementRef.current
    ) {
      // Set documentElement properties: (essentially scroll to top and left).
      // - assign scroll behavior auto.
      // - assign 0 to documentElement scrollTop and scrollLeft.
      // - remove the scroll behavior property.
      documentElementRef.current.style.setProperty('scroll-behavior', 'auto');
      documentElementRef.current.scrollTop = 0;
      documentElementRef.current.scrollLeft = 0;
      documentElementRef.current.style.removeProperty('scroll-behavior');

      // Clear the vert and hors data refs.
      lastScrolledVertDataRef.current = undefined;
      lastScrolledHorzDataRef.current = undefined;
    }

    // If the current resourceId does not match the previous one AND exercise is done...
    if (resource && resource._id !== resourceRef?.current?._id && outOfTries) {
      // Get the correct answer from the timeline.
      const correctResource = annotations?.find(
        ({ subType }) => subType === AnnotationTypes.Correct,
      );
      // Create a new resource based on the correct answer, assign the correct/incorrect
      // class based on user selection, and mark as active.
      const resource = {
        ...correctResource,
        className: stateType === 'correct' ? AnnotationTypes.Correct : AnnotationTypes.Incorrect,
        active: true,
      };

      // Update the resource with newly created one. Do not show paragraph marks.
      setTimeout(() => updateResource(resource));
      setShowParagraphMarks(false);
    }

    // Prevent autoscrolling on Feedback of CATA
    if (slideType === TemplateTypes.CheckAllApply) {
      ignoreAutoScroll = true;
    }

    // If scrolling is not ignored and this is a non-CBS slide or
    // it IS a CBS slide, but activity is completed...
    if (
      !ignoreAutoScroll &&
      (slideType !== TemplateTypes.ClickBestSpot ||
        (slideType === TemplateTypes.ClickBestSpot && outOfTries))
    ) {
      // Scroll to the annotation.
      getAnnotationOffscreenPositionAndScroll();
    }

    const activeTooltipAnnotations = annotations.filter(
      (a) => a.active && a.subType === AnnotationTypes.Tooltip,
    );
    const idsToDestroy = Object.keys(annotationIdToTippyInstance.current || {});
    for (const tooltipAnnotation of activeTooltipAnnotations) {
      // Iterate through the active tooltips and remove anything that's active
      // from idsToRemove. Afterwards, destroy any remaining since those are no
      // longer active
      const startIndex = tooltipAnnotation.startIndex as number;
      const endIndex = tooltipAnnotation.endIndex as number;
      const id = tooltipAnnotation._id as string;
      const index = idsToDestroy.findIndex((i) => i === id);
      const isLayered = isLayeredAnnotation(tooltipAnnotation);

      // If found, remove the id from the list of ids to destroy
      if (index > -1) {
        idsToDestroy.splice(index, 1);
      }
      // if the id wasn't found, this is a new tooltip, so create an instance for it
      else {
        const selectors = [];
        for (let i = startIndex; i <= endIndex; i += 1) {
          const id = isLayered ? `${tooltipAnnotation.rootId}-${i}` : `${i}`;
          selectors.push(`.Tooltip[data-id="${id}"]`);
        }

        annotationIdToTippyInstance.current[id] = tippy(selectors.join(','), {
          trigger: 'click',
          placement: 'auto',
          theme: 'praktio',
          duration: [300, 0],
        });
      }
    }

    for (const idToDestroy of idsToDestroy) {
      for (const tippyInstance of annotationIdToTippyInstance.current[idToDestroy]) {
        tippyInstance.destroy();
      }
    }

    resourceRef.current = resource;
  }, [
    lastScrolledVertDataRef,
    lastScrolledHorzDataRef,
    documentElementRef,
    outOfTries,
    paragraphSelection,
    resource,
    selected,
    stateType,
    slideType,
    annotations,
    scrollToAnnotation,
    getAnnotationOffscreenPositionAndScroll,
  ]);

  // Doc Content Creation
  let content: string | undefined;
  if (isBestEdit && isBestEditContent) {
    content = isBestEditContent;
  } else if (AnnotationEnabledTypes.includes(slideType || '')) {
    if (resource?.version === 2 && resourceContent && annotations) {
      content = processDocument_v2({
        content: resourceContent,
        annotations,
        slideType,
        isLive: true,
        showParagraphMarks,
        selected,
        paragraphSelection,
        outOfTries,
      });
    } else if (resourceContent && annotations) {
      content = addAnnotationsToContent(
        resourceContent,
        annotations,
        slideType,
        true,
        showParagraphMarks,
        selected,
      );
    }
  } else {
    content = resourceContent;
  }

  const contentIsOnlySpaces = content === '';

  const overlay =
    slideType === TemplateTypes.Explanation
      ? annotations?.find(
          (annotation) => annotation.subType === AnnotationTypes.Overlay && annotation.active,
        )
      : undefined;

  let firstLoremLength;
  if (excerptPosition === ExcerptPositions.Beginning) {
    firstLoremLength = LoremLengths.None;
  } else if (excerptPosition === ExcerptPositions.Middle) {
    firstLoremLength = LoremLengths.Short;
  } else {
    firstLoremLength = LoremLengths.Long;
  }

  const secondLoremLength =
    excerptPosition === ExcerptPositions.End ? LoremLengths.None : LoremLengths.Long;

  content = isCBSSlide(slideType) ? content : wrapElementsInAnnotationSpan(content); // FIXME: Temporarily excluding CBS from this. Will revisit when working on the CBS ADA updates

  return (
    <>
      <Document
        answeredClass={answeredClass}
        baseClass={resource?.baseClass}
        className={classNames({ 'h-100': contentIsOnlySpaces })}
        docRef={documentElementRef}
        isBestEdit={isBestEdit}
        isExcerpt={isExcerpt}
        onMouseUp={onSelect ? onSelect : () => {}}
        sharedWidth={sharedWidth}
        shouldPulse={shouldPulse}
        showExplain={showExplain}
        singlePanel={singlePanel}
        slideType={slideType}
        style={null}
        symbolClass={null}
        textClass={textClass}
        triPanel={triPanel}
        windowSize={windowSize}
        disabled={mainDocDisabled}
      >
        {textClass === 'email' && <div className="new-message">New Message</div>}
        {isExcerpt && !hideExcerptLabel && (
          <LoremComponent loremLength={firstLoremLength} windowSize={windowSize} />
        )}
        {htmlParser(cleanContent(content || ''))}
        {isExcerpt && !hideExcerptLabel && (
          <LoremComponent loremLength={secondLoremLength} windowSize={windowSize} />
        )}
      </Document>
      {isExcerpt && (
        <div
          className={classNames('excerpt-label', {
            triPanel: triPanel && !isBestEdit,
          })}
        >
          Excerpt
        </div>
      )}
      {!!closestOffTop && (
        <ScrollToAnnotationButton
          position={AnnotationOffscreenPositions.Top}
          onClick={() =>
            scrollToSpecificAnnotation({
              closestOffData: closestOffTop,
              position: AnnotationOffscreenPositions.Top,
            })
          }
          supplementalDocState={supplementalDocState}
          hasSupplementalDoc={hasSupplementalDoc}
          docIsLandscape={!!resource?.isLandscape}
        />
      )}
      {!!closestOffBottom && (
        <ScrollToAnnotationButton
          position={AnnotationOffscreenPositions.Bottom}
          onClick={() =>
            scrollToSpecificAnnotation({
              closestOffData: closestOffBottom,
              position: AnnotationOffscreenPositions.Bottom,
            })
          }
          supplementalDocState={supplementalDocState}
          hasSupplementalDoc={hasSupplementalDoc}
          docIsLandscape={!!resource?.isLandscape}
        />
      )}
      {!!closestOffLeft && (
        <ScrollToAnnotationButton
          position={AnnotationOffscreenPositions.Left}
          onClick={() =>
            scrollToSpecificAnnotation({
              closestOffData: closestOffLeft,
              position: AnnotationOffscreenPositions.Left,
            })
          }
          supplementalDocState={supplementalDocState}
          hasSupplementalDoc={hasSupplementalDoc}
          docIsLandscape={!!resource?.isLandscape}
        />
      )}
      {!!closestOffRight && (
        <ScrollToAnnotationButton
          position={AnnotationOffscreenPositions.Right}
          onClick={() =>
            scrollToSpecificAnnotation({
              closestOffData: closestOffRight,
              position: AnnotationOffscreenPositions.Right,
            })
          }
          supplementalDocState={supplementalDocState}
          hasSupplementalDoc={hasSupplementalDoc}
          docIsLandscape={!!resource?.isLandscape}
        />
      )}

      {enableParagraphMarks && (
        <Button
          aria-hidden
          className={classNames('paragraph-marks', { isExcerpt })}
          onClick={toggleParagraphMarks}
          active={showParagraphMarks}
        >
          <i className="fas fa-paragraph"></i>
        </Button>
      )}
      {overlay && overlay.active && (
        <div className="overlay-annotation">
          <div className="overlay-wrapper">
            <div className="overlay-content">{htmlParser(cleanContent(overlay.content || ''))}</div>
          </div>
        </div>
      )}
    </>
  );
};

export default TextLoader;
